import type {
  ApiKeyOperationResult,
  ApiKeyRevealResult,
  Business,
  BusinessCategoryCode,
  BusinessDetailsView,
  BusinessStatus,
  BusinessSummary,
  CreatePharmacyInput,
  LicenseDeleteInput,
  LicenseDeleteResult,
  LicenseIssueInput,
  LicenseOperationResult,
  LicenseRenewInput,
  LicenseRevealInput,
  LicenseRevealResult,
  OwnerAccessScope,
  OwnerAccessUpdateResult,
  OwnerCreateResult,
  OwnerOperationResult,
  OwnerResetPasswordResult,
  PageResult,
  Pharmacy,
  ProductionLinkStatus,
  ProvisioningPlan,
  ProvisioningRunResult,
  ProvisioningSecrets,
  ProvisioningView,
  UpdateCustomerOperationResult,
  UpdateDeploymentOperationResult,
} from '@quds/shared';
import { apiGet, apiPatch, apiPost } from '@/lib/api';

export interface BusinessListItem extends BusinessSummary {
  branchCount: number;
  production: { linked: boolean; productionLinkStatus: ProductionLinkStatus };
}

export interface BusinessListQuery {
  category?: BusinessCategoryCode;
  search?: string;
  country?: string;
  status?: BusinessStatus;
  sort?: 'name' | 'country' | 'status' | 'createdAt' | 'code';
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export const listBusinesses = (q: BusinessListQuery = {}) =>
  apiGet<PageResult<BusinessListItem>>('/businesses', {
    category: q.category,
    search: q.search?.trim(),
    country: q.country,
    status: q.status,
    sort: q.sort,
    order: q.order,
    page: q.page,
    pageSize: q.pageSize,
  });

export const getBusiness = (id: string) => apiGet<Business>(`/businesses/${encodeURIComponent(id)}`);

export const getBusinessDetails = (id: string) =>
  apiGet<BusinessDetailsView>(`/businesses/${encodeURIComponent(id)}/details`);

export const issueBusinessLicense = (businessId: string, body: LicenseIssueInput) =>
  apiPost<LicenseOperationResult>(`/businesses/${encodeURIComponent(businessId)}/licenses/issue`, body);

export const renewBusinessLicense = (businessId: string, licenseId: string, body: LicenseRenewInput) =>
  apiPost<LicenseOperationResult>(
    `/businesses/${encodeURIComponent(businessId)}/licenses/${encodeURIComponent(licenseId)}/renew`,
    body,
  );

export const revealBusinessLicense = (businessId: string, licenseId: string, body: LicenseRevealInput) =>
  apiPost<LicenseRevealResult>(
    `/businesses/${encodeURIComponent(businessId)}/licenses/${encodeURIComponent(licenseId)}/reveal`,
    body,
  );

export const deleteBusinessLicense = (businessId: string, licenseId: string, body: LicenseDeleteInput) =>
  apiPost<LicenseDeleteResult>(
    `/businesses/${encodeURIComponent(businessId)}/licenses/${encodeURIComponent(licenseId)}/delete`,
    body,
  );

export const createBusinessApiKey = (
  businessId: string,
  branchId: string,
  body: { label?: string; idempotencyKey: string; intent?: 'first' | 'additional' },
) =>
  apiPost<ApiKeyOperationResult>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/api-keys`,
    body,
  );

export const rotateBusinessApiKey = (
  businessId: string,
  branchId: string,
  keyId: string,
  body: { label?: string; idempotencyKey: string },
) =>
  apiPost<ApiKeyOperationResult>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/api-keys/${encodeURIComponent(keyId)}/rotate`,
    body,
  );

export const deactivateBusinessApiKey = (
  businessId: string,
  branchId: string,
  keyId: string,
  body: { idempotencyKey: string },
) =>
  apiPost<ApiKeyOperationResult>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/api-keys/${encodeURIComponent(keyId)}/deactivate`,
    body,
  );

export const revealBusinessApiKey = (
  businessId: string,
  branchId: string,
  keyId: string,
  body: { idempotencyKey: string },
) =>
  apiPost<ApiKeyRevealResult>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/api-keys/${encodeURIComponent(keyId)}/reveal`,
    body,
  );

export const setBusinessUpdateChannel = (businessId: string, body: { assignedChannel: 'stable' | 'rc' | 'beta'; idempotencyKey: string }) =>
  apiPost<UpdateCustomerOperationResult>(`/businesses/${encodeURIComponent(businessId)}/updates/channel`, body);

export const enableBusinessUpdates = (businessId: string, body: { idempotencyKey: string }) =>
  apiPost<UpdateCustomerOperationResult>(`/businesses/${encodeURIComponent(businessId)}/updates/enable`, body);

export const disableBusinessUpdates = (businessId: string, body: { idempotencyKey: string }) =>
  apiPost<UpdateCustomerOperationResult>(`/businesses/${encodeURIComponent(businessId)}/updates/disable`, body);

export const deployBusinessUpdate = (
  businessId: string,
  body: { releaseId: string; name: string; activate?: boolean; updateDeviceId?: string; idempotencyKey: string },
) =>
  apiPost<UpdateDeploymentOperationResult>(`/businesses/${encodeURIComponent(businessId)}/updates/deployments`, body);

export const activateBusinessOwner = (businessId: string, ownerId: string, body: { idempotencyKey: string }) =>
  apiPost<OwnerOperationResult>(`/businesses/${encodeURIComponent(businessId)}/owners/${encodeURIComponent(ownerId)}/activate`, body);

export const deactivateBusinessOwner = (businessId: string, ownerId: string, body: { idempotencyKey: string }) =>
  apiPost<OwnerOperationResult>(`/businesses/${encodeURIComponent(businessId)}/owners/${encodeURIComponent(ownerId)}/deactivate`, body);

// --------------------------------------------------------------- Batch A

export const getProvisioning = (businessId: string) =>
  apiGet<ProvisioningView>(`/businesses/${encodeURIComponent(businessId)}/provisioning`);

export const startProvisioning = (businessId: string, plan: ProvisioningPlan) =>
  apiPost<ProvisioningRunResult>(`/businesses/${encodeURIComponent(businessId)}/provisioning`, plan);

export const createProductionBranch = (
  businessId: string,
  body: { branch: { branchRef: string; name: string; primary?: boolean; localBranchId?: string }; createApiKeyForFirstBranch?: boolean },
) => apiPost<ProvisioningRunResult>(`/businesses/${encodeURIComponent(businessId)}/branches/production`, body);

export const revealBranchEnrollment = (businessId: string, branchId: string, body: { idempotencyKey: string }) =>
  apiPost<{ branchId: string; enrollmentCode: string | null; requestId: string }>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/enrollment/reveal`,
    body,
  );

export const updateProductionTenant = (
  businessId: string,
  body: { name?: string; businessType?: string; idempotencyKey: string },
) =>
  apiPatch<{ tenantId: string; name: string; businessType: string; requestId: string }>(
    `/businesses/${encodeURIComponent(businessId)}/production/tenant`,
    body,
  );

export const updateProductionBranch = (
  businessId: string,
  branchId: string,
  body: { name?: string; branchRef?: string; isActive?: boolean; idempotencyKey: string },
) =>
  apiPatch<{ branchId: string; name: string; branchRef: string; isActive: boolean; requestId: string }>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/production`,
    body,
  );

export interface BranchInventoryPage {
  items: Array<{
    sku?: string;
    barcode?: string | null;
    itemName?: string;
    name?: string;
    price?: number | string | null;
    stock?: number | string | null;
    quantity?: number | string | null;
    expiryDate?: string | null;
    updatedAt?: string | null;
    [key: string]: unknown;
  }>;
  page: number;
  limit: number;
  total: number | null;
  nextCursor: string | null;
  requestId: string;
  fetchedAt: string;
  stale: boolean;
}

export const listBranchInventory = (
  businessId: string,
  branchId: string,
  query: { page?: number; limit?: number; q?: string } = {},
) =>
  apiGet<BranchInventoryPage>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/inventory`,
    query,
  );

export interface SyncBranchListItem {
  id: string;
  tenantId: string;
  tenantName: string;
  branchRef: string;
  name: string;
  isActive: boolean;
  lastSyncedAt: string | null;
  lastSeenAt: string | null;
  estimatedOnlineStatus: 'ONLINE' | 'STALE' | 'OFFLINE';
  itemCount: number;
  businessId: string | null;
  businessName: string | null;
  businessCode: string | null;
  businessCountry?: string | null;
}

export interface SyncBranchListResult {
  items: SyncBranchListItem[];
  nextCursor: string | null;
  limit: number;
  semantics: { ONLINE: string; STALE: string; OFFLINE: string };
  requestId: string;
  fetchedAt: string;
  stale: boolean;
}

export const listSyncBranches = (query: {
  q?: string;
  status?: 'ONLINE' | 'STALE' | 'OFFLINE';
  limit?: number;
  cursor?: string;
} = {}) => apiGet<SyncBranchListResult>('/sync/branches', query);

export const createBusinessOwnerAccount = (
  businessId: string,
  body: {
    username: string;
    fullName: string;
    temporaryPassword?: string;
    accessScope?: OwnerAccessScope;
    /** Central Admin branch id (resolved to Production by the API). */
    branchId?: string | null;
    idempotencyKey: string;
  },
) => apiPost<OwnerCreateResult>(`/businesses/${encodeURIComponent(businessId)}/owners`, body);

export const updateBusinessOwnerAccess = (
  businessId: string,
  ownerId: string,
  body: {
    accessScope: OwnerAccessScope;
    /** Central Admin branch id (resolved to Production by the API). */
    branchId?: string | null;
    idempotencyKey: string;
  },
) =>
  apiPost<OwnerAccessUpdateResult>(
    `/businesses/${encodeURIComponent(businessId)}/owners/${encodeURIComponent(ownerId)}/access`,
    body,
  );

export const resetBusinessOwnerPassword = (businessId: string, ownerId: string, body: { idempotencyKey: string }) =>
  apiPost<OwnerResetPasswordResult>(`/businesses/${encodeURIComponent(businessId)}/owners/${encodeURIComponent(ownerId)}/reset-password`, body);

export interface ProvisionPharmacyInput extends CreatePharmacyInput {
  createProductionEnvironment: boolean;
  production?: {
    additionalBranches?: { name: string; referenceCode: string; type: 'main' | 'secondary' | 'warehouse' }[];
    createApiKeyForFirstBranch?: boolean;
    owner?: { username: string; fullName: string; temporaryPassword?: string };
  };
}

export interface ProvisionResponse {
  pharmacy: Pharmacy;
  createdProduction: boolean;
  provisioning?: ProvisioningView;
  secrets?: ProvisioningSecrets;
  tenantId?: string | null;
}

export const provisionBusiness = (body: ProvisionPharmacyInput) => apiPost<ProvisionResponse>('/businesses/provision', body);

export const getBranchPosControl = (businessId: string, branchId: string) =>
  apiGet<Record<string, unknown>>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/pos-control`,
  );

export const setPosOperationalStatus = (
  businessId: string,
  branchId: string,
  body: { status: 'ACTIVE' | 'SUSPENDED'; reason?: string | null },
) =>
  apiPost<Record<string, unknown>>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/pos-control/status`,
    body,
  );

export const createPosCredentialCommand = (
  businessId: string,
  branchId: string,
  body: {
    localUserId: string;
    username?: string | null;
    password?: string | null;
    allowInactive?: boolean;
  },
) =>
  apiPost<Record<string, unknown>>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/pos-control/credentials`,
    body,
  );

export const createPosUserCommand = (
  businessId: string,
  branchId: string,
  body: {
    fullName: string;
    username: string;
    password: string;
    role: 'admin' | 'pharmacist' | 'cashier';
  },
) =>
  apiPost<Record<string, unknown>>(
    `/businesses/${encodeURIComponent(businessId)}/branches/${encodeURIComponent(branchId)}/pos-control/users`,
    body,
  );
